import { Box, BoxProps, Button, FormikTextInput, Icon, Spinner } from '@palmetto/palmetto-components';
import { Field, Form, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatKWHNumber } from '../../helpers/kwhFormatter';
import { percentageFormatter } from '../../helpers/percentageFormatter';
import {
  CheckButtonProps,
  CommonFormSectionProps,
  FormFooterActionProps,
  FormFooterProps,
  FormSectionProps,
  IInputFormProps,
  LoadingState,
  Origin,
} from '../../types/ProductionValueCheck';
import FormSection from '../Forms/FormSection';
import FormSectionContainer from '../Forms/FormSectionContainer';

export function InputForm({
  onChange,
  errorObject,
  loadingState,
  activeQuote,
  origin,
  confirmObject,
}: IInputFormProps) {
  const { systemSizeChangeCalculator = false } = useFlags();
  const { setFieldValue, values } = useFormikContext<any>();

  const currentSFYPKwh = activeQuote ? activeQuote.systemFirstYearProductionKwh : 0;
  const currentSystemSizeKw = activeQuote ? activeQuote.systemSizeKw : 0;

  const setValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFieldValue(event.target.name, event.target.value);
      onChange();
    },
    // eslint-disable-next-line
    [setFieldValue, values, onChange],
  );

  const isFromInstallPackage = origin === Origin.INSTALL_PACKAGE;
  const installedEstimateLabel = isFromInstallPackage
    ? 'Installed Year-1 Total Production Estimate'
    : 'New Expected Total Year-1 Production';

  return (
    <Form noValidate>
      <FormSectionContainer>
        <FormSection>
          <Box direction={{ base: 'column', tablet: 'row' }} gap="lg" flex="auto" justifyContent="space-between">
            <Box>
              <CommonFormSection title="Contracted Year-1 Total Production Estimate">
                <Box fontWeight="medium" fontSize="lg">
                  {formatKWHNumber(currentSFYPKwh)} kWh
                </Box>
              </CommonFormSection>
              <CommonFormSection
                title={installedEstimateLabel}
                description="If any change to the PV system design has occurred between sale and install, input the updated production number here to determine if a new contract is required."
              >
                <Field
                  name="systemFirstYearProductionKwh"
                  id="systemFirstYearProductionKwh"
                  component={FormikTextInput}
                  isDisabled={somethingIsUpdating(loadingState)}
                  suffix="kWh"
                  maxWidth="2xl"
                  onChange={setValue}
                />
              </CommonFormSection>
            </Box>
            {systemSizeChangeCalculator && (
              <Box>
                <CommonFormSection title="Contracted System Size">
                  <Box fontWeight="medium" fontSize="lg">
                    {currentSystemSizeKw} kW
                  </Box>
                </CommonFormSection>
                <CommonFormSection title="New Expected DC System Size" baseStyle={{ padding: 'lg 0 0 0' }}>
                  <Field
                    name="systemSizeKw"
                    id="systemSizeKw"
                    component={FormikTextInput}
                    isDisabled={somethingIsUpdating(loadingState)}
                    suffix="kW"
                    maxWidth="2xl"
                    onChange={setValue}
                  />
                </CommonFormSection>
              </Box>
            )}
          </Box>
        </FormSection>
        <DifferenceFormSection errorObject={errorObject} confirmObject={confirmObject} />
        <FormFooter
          origin={origin}
          confirmObject={confirmObject}
          loadingState={loadingState}
          errorObject={errorObject}
        />
      </FormSectionContainer>
    </Form>
  );
}

export function somethingIsLoading(loadingState: LoadingState) {
  return Object.values(loadingState).includes(true);
}

export function somethingIsUpdating(loadingState: LoadingState) {
  return loadingState.isUpdating || loadingState.isVoiding;
}

export const DifferenceFormSection = ({ errorObject, confirmObject }: FormSectionProps) => {
  const { systemSizeChangeCalculator = false } = useFlags();
  const hasError = Boolean(errorObject?.production) || Boolean(errorObject?.systemSize);
  const hasConfirm = Boolean(confirmObject?.production) || Boolean(confirmObject?.systemSize);
  if (!hasError && !hasConfirm) {
    return null;
  }

  const hasConfirmationWithoutError = hasConfirm && !hasError;
  const productionLabel = (() => {
    if (errorObject?.production) {
      return `${systemSizeChangeCalculator ? 'Production: ' : ''}${percentageFormatter.format(
        errorObject.production.percentErrorDiff,
      )} (${formatKWHNumber(errorObject.production.errorKwH)} kWh)`;
    }
    if (confirmObject?.production) {
      return `${systemSizeChangeCalculator ? 'Production: ' : ''}${percentageFormatter.format(
        confirmObject.production.percentDiff,
      )} (${formatKWHNumber(confirmObject.production.diffKwH)} kWh)`;
    }
    return null;
  })();
  const productionColor = errorObject?.production ? 'danger' : 'contrast';
  const systemSizeLabel = (() => {
    if (errorObject?.systemSize) {
      return `System Size: ${percentageFormatter.format(
        errorObject.systemSize.percentErrorDiff,
      )} (${Number(errorObject.systemSize.errorKw).toFixed(2)} kW)`;
    }
    if (confirmObject?.systemSize) {
      return `System Size: ${percentageFormatter.format(
        confirmObject.systemSize.percentDiff,
      )} (${Number(confirmObject.systemSize.diffKw).toFixed(2)} kW)`;
    }
    return null;
  })();
  const systemSizeColor = errorObject?.systemSize ? 'danger' : 'contrast';

  return (
    <CommonFormSection
      title="Difference"
      baseStyle={{
        padding: 'md 0 lg 0',
        borderColor: 'separator',
      }}
    >
      <Box width="100">
        <Box
          fontWeight="medium"
          fontSize="lg"
          data-testid="pvcheck-input-form-diff-lbl"
          alignItems="flex-start"
          childGap="xs"
          display={{ base: 'block', tablet: 'grid' }}
          style={{ gridTemplateColumns: '1fr 1fr' }}
        >
          {productionLabel && <Box color={productionColor}>{productionLabel}</Box>}
          {systemSizeChangeCalculator && systemSizeLabel && <Box color={systemSizeColor}>{systemSizeLabel}</Box>}
        </Box>

        {hasConfirmationWithoutError && (
          <Box fontSize="sm" margin="sm 0 0 0" display="flex" direction="row" color="success-500">
            <Icon name="check" size="sm" color="success" />
            <Box as="span" style={{ marginLeft: '0.2em' }}>
              No change order required, as it is within the allowable range.
            </Box>
          </Box>
        )}
      </Box>
    </CommonFormSection>
  );
};

export function CheckButton({ confirmObject, loadingState, origin, errorObject }: CheckButtonProps) {
  const { systemSizeChangeCalculator = false } = useFlags();
  const comesFromInstallMilestone = origin === Origin.INSTALL_PACKAGE;
  const comesFromPVSystemTab = origin === Origin.PV_SYSTEM_TAB;
  const isWorking = loadingState.isChecking || loadingState.isVoiding || loadingState.isUpdating;
  const isLoading = somethingIsLoading(loadingState);
  const hasError = Boolean(errorObject?.production) || Boolean(errorObject?.systemSize);
  const systemSizeDifference = confirmObject?.systemSize?.percentDiff;
  const productionDifference = confirmObject?.production?.percentDiff;
  const MIN_THRESHOLD = -0.05;
  const MAX_THRESHOLD = 0.1;
  const differencesToCheck = systemSizeChangeCalculator
    ? [systemSizeDifference, productionDifference]
    : [productionDifference];
  const areProductionAndSystemSizeInRange = differencesToCheck.every(
    (value) => value != null && value >= MIN_THRESHOLD && value <= MAX_THRESHOLD,
  );

  if (comesFromInstallMilestone) {
    return (
      <Button
        variant="primary"
        type="submit"
        isLoading={isWorking}
        iconSuffix="arrow-right"
        disabled={isLoading || hasError}
        data-testid="frm-foot-check-cta"
      >
        Next
      </Button>
    );
  }
  if (areProductionAndSystemSizeInRange || (confirmObject && comesFromPVSystemTab)) {
    return (
      <Button
        variant="primary"
        type="submit"
        isLoading={isWorking}
        disabled={isLoading || hasError}
        data-testid="frm-foot-check-cta"
      >
        Save
      </Button>
    );
  }
  return (
    <Button
      variant="primary"
      type="submit"
      isLoading={isWorking}
      disabled={isLoading}
      data-testid="frm-foot-check-cta"
    >
      Check
    </Button>
  );
}

export const FormFooterActions = ({ loadingState, origin, confirmObject, errorObject }: FormFooterActionProps) => {
  const navigate = useNavigate();
  function goBack() {
    navigate(-1);
  }
  const loading = somethingIsLoading(loadingState);

  return (
    <Box
      {...boxStyle}
      width={{
        base: '100',
        tablet: 'auto',
      }}
    >
      <Button onClick={goBack} variant="secondary" tone="neutral" isDisabled={loading}>
        Cancel
      </Button>
      <CheckButton
        confirmObject={confirmObject}
        loadingState={loadingState}
        origin={origin}
        errorObject={errorObject}
      />
    </Box>
  );
};

export const FormFooter = ({ loadingState, origin, confirmObject, errorObject }: FormFooterProps) => {
  return (
    <Box {...boxStyle} justifyContent={boxStyle.justifyContent} alignItems={'center'}>
      {loadingState.isChecking && (
        <Box width={'100%'} display={'flex'} direction={'row'} alignItems={'center'}>
          <Box data-testid="frm-foot-spin-checking-spinn">
            <Spinner size="lg" color="info" />
          </Box>
          <Box padding={'0 sm 0 sm'} fontSize={'md'} color="body-primary">
            Validating new year-1 production estimate is within acceptable tolerance...
          </Box>
        </Box>
      )}
      <FormFooterActions
        confirmObject={confirmObject}
        loadingState={loadingState}
        origin={origin}
        errorObject={errorObject}
      />
    </Box>
  );
};

const boxStyle: BoxProps = {
  direction: {
    base: 'column',
    tablet: 'row',
  },
  alignItems: {
    base: 'stretch',
    tablet: 'flex-end',
  },
  justifyContent: {
    tablet: 'flex-end',
  },
  childGap: 'sm',
  style: { flexShrink: 0 },
  position: 'relative',
  padding: 'lg 0 0 0',
};

export function CommonFormSection({ baseStyle = {}, children, description, title }: CommonFormSectionProps) {
  return (
    <FormSection
      title={title}
      description={description}
      baseStyle={{
        padding: 'md 0',
        borderColor: 'transparent',
        ...baseStyle,
      }}
    >
      <Box
        data-testid="comm-frm-section"
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        childGap={{ base: 'lg', desktop: 'xl' }}
      >
        {children}
      </Box>
    </FormSection>
  );
}
